import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Share,
  Delete as DeleteIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField as MuiTextField } from '@mui/material';
// Note: Redux dispatches removed as this component now uses local state
import { useNavigate } from 'react-router-dom';
import { formatDateSafe, getBestDate, createDateSafe, formatFileSize as formatFileSizeUtil } from '../../utils/dateUtils';

// Props interface for the component
interface DocumentListViewProps {
  documents?: any[];
  loading?: boolean;
  loadingMore?: boolean;
  onDocumentClick?: (documentId: string) => void;
  searchQuery?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: {
    categories: string[];
    tags: string[];
    dateRange: { start: Date | null; end: Date | null };
    uploadedBy: string[];
    fileTypes: string[];
  };
  // Infinite scroll props
  hasMore?: boolean;
  onLoadMore?: () => void;
  totalCount?: number;
  onDelete?: (documentId: string) => void;
  onUpdateTags?: (documentId: string, tags: string[]) => void;
}

// Note: Using any[] for documents from DocumentsPage to avoid type conflicts

const DocumentListView: React.FC<DocumentListViewProps> = ({
  documents = [],
  loading = false,
  loadingMore = false,
  onDocumentClick,
  searchQuery = '',
  sortBy = 'date',
  sortOrder = 'desc',
  filters,
  hasMore = false,
  onLoadMore,
  totalCount = 0,
  onDelete,
  onUpdateTags,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [tagEditOpen, setTagEditOpen] = useState(false);
  const [tagEditDocId, setTagEditDocId] = useState<string | null>(null);
  const [tagEditValue, setTagEditValue] = useState<string[]>([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Local state for UI controls
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [error] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // スクロールイベントのスロットリング用タイマーref
  const scrollThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 無限スクロール実装（スロットリング付き）
  const handleScroll = useCallback(() => {
    if (!hasMore || loading || loadingMore || !onLoadMore) return;

    // 既にスロットリング中の場合はスキップ
    if (scrollThrottleRef.current) return;

    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // ページ下部200px手前で追加読み込みをトリガー
    if (scrollTop + windowHeight >= documentHeight - 200) {
      onLoadMore();
      // 500msのスロットリングを適用（重複リクエスト防止）
      scrollThrottleRef.current = setTimeout(() => {
        scrollThrottleRef.current = null;
      }, 500);
    }
  }, [hasMore, loading, loadingMore, onLoadMore]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // クリーンアップ時にスロットリングタイマーをクリア
      if (scrollThrottleRef.current) {
        clearTimeout(scrollThrottleRef.current);
        scrollThrottleRef.current = null;
      }
    };
  }, [handleScroll]);

  // フィルタリングされたドキュメント
  const filteredDocuments = documents.filter((doc) => {
    let matches = true;

    if (searchQuery) {
      matches = matches && (
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
        (doc.tags?.some((tag: string) => tag.toLowerCase().includes(searchQuery.toLowerCase())) ?? false)
      );
    }

    if (filters) {
      // Category filtering
      if (filters.categories.length > 0) {
        matches = matches && filters.categories.includes(doc.category);
      }

      // Tags filtering
      if (filters.tags.length > 0) {
        matches = matches && doc.tags?.some((tag: string) =>
          filters.tags.includes(tag)
        );
      }

      // File type filtering
      if (filters.fileTypes.length > 0) {
        matches = matches && filters.fileTypes.includes(doc.fileType);
      }

      // Uploaded by filtering
      if (filters.uploadedBy.length > 0) {
        matches = matches && filters.uploadedBy.includes(doc.uploadedBy);
      }

      // Date range filtering
      if (filters.dateRange.start || filters.dateRange.end) {
        const dateString = getBestDate(doc);
        const docDate = createDateSafe(dateString);
        if (docDate) {
          if (filters.dateRange.start) {
            matches = matches && docDate >= filters.dateRange.start;
          }
          if (filters.dateRange.end) {
            matches = matches && docDate <= filters.dateRange.end;
          }
        }
      }
    }

    return matches;
  });

  // ソート処理
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let compareValue = 0;

    switch (sortBy) {
      case 'title':
      case 'name':
        compareValue = a.title.localeCompare(b.title);
        break;
      case 'date':
      case 'updatedAt':
        const dateA = createDateSafe(getBestDate(a)) || new Date(0);
        const dateB = createDateSafe(getBestDate(b)) || new Date(0);
        compareValue = dateA.getTime() - dateB.getTime();
        break;
      case 'size':
      case 'fileSize':
        compareValue = a.size - b.size;
        break;
      case 'category':
        compareValue = a.category.localeCompare(b.category);
        break;
      default:
        compareValue = 0;
    }

    return sortOrder === 'asc' ? compareValue : -compareValue;
  });

  // Note: Sort and view mode controls moved to parent DocumentsPage

  const handleDocumentClick = (documentId: string) => {
    if (onDocumentClick) {
      onDocumentClick(documentId);
    } else {
      navigate(`/documents/${documentId}`);
    }
  };

  // const handleDownload = async (event: React.MouseEvent, document: any) => {
  //   event.stopPropagation();
  //   // ダウンロード処理を実装
  //   console.log('Download document:', document.id);
  // };

  const handleShare = async (event: React.MouseEvent, document: any) => {
    event.stopPropagation();
    // 共有モーダルを開く処理を実装
    console.log('Share document:', document.id);
    setShareModalOpen(true);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDocuments(sortedDocuments.map(doc => doc.id));
    } else {
      setSelectedDocuments([]);
    }
  };

  const handleSelectDocument = (documentId: string) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  // Use utility functions for date and file size formatting

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress role="progressbar" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (documents.length === 0 && !loading) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" color="text.secondary">
          {t('documents.noDocumentsFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3} data-testid={isMobile ? 'document-list-mobile' : 'document-list-desktop'}>
      {/* Note: Header controls moved to parent DocumentsPage */}

      {/* 全選択チェックボックス */}
      <Box mb={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedDocuments.length === sortedDocuments.length && sortedDocuments.length > 0}
              indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < sortedDocuments.length}
              onChange={handleSelectAll}
              data-testid="select-all-checkbox"
            />
          }
          label={t('common.selectAll')}
        />
      </Box>

      {/* ドキュメントリスト */}
      {/* Always render as list view since this is DocumentListView */}
      <Box>
        {sortedDocuments.map((document) => (
          <Card key={document.id} sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid size="auto">
                  <Checkbox
                    checked={selectedDocuments.includes(document.id)}
                    onChange={() => handleSelectDocument(document.id)}
                  />
                </Grid>
                <Grid size="grow">
                  <Typography
                    variant="h6"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleDocumentClick(document.id)}
                  >
                    {document.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {document.summary || document.description}
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap" alignItems="center">
                    <Chip
                      label={document.category || t('documents.generalCategory')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />

                    {/* 分析済みタグの表示 */}
                    {document.analysisHistory && document.analysisHistory.length > 0 && (
                      <Chip
                        label={t('documents.deepAnalyzed')}
                        size="small"
                        color="secondary"
                        sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}
                      />
                    )}

                    {document.tags && document.tags.length > 0 &&
                      document.tags.map((tag: string, index: number) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                        />
                      ))
                    }

                    {document.version && <Chip label={`v${document.version}`} size="small" variant="outlined" />}
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSizeUtil(document.size || document.fileSize)} • {formatDateSafe(getBestDate(document))}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size="auto">
                  <Box display="flex" gap={1}>
                    {/* <Tooltip title="Download">
                      <IconButton
                        onClick={(e) => handleDownload(e, document)}
                        data-testid="download-button"
                      >
                        <GetApp />
                      </IconButton>
                    </Tooltip> */}
                    <Tooltip title={t('common.share')}>
                      <IconButton
                        onClick={(e) => handleShare(e, document)}
                        data-testid="share-button"
                      >
                        <Share />
                      </IconButton>
                    </Tooltip>
                    {onUpdateTags && (
                      <Tooltip title={t('chat.editTags')}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setTagEditDocId(document.id);
                            setTagEditValue(document.tags || []);
                            setTagEditOpen(true);
                          }}
                          size="small"
                        >
                          <TagIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetId(document.id);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}

        {/* 無限スクロールの読み込みインジケーターと手動読み込みボタン */}
        {hasMore && (
          <Box display="flex" flexDirection="column" alignItems="center" p={3}>
            {loadingMore ? (
              <CircularProgress />
            ) : (
              <Button
                variant="outlined"
                onClick={onLoadMore}
                disabled={loading || loadingMore}
                sx={{ minWidth: 200 }}
              >
                {t('documents.loadMore')}
              </Button>
            )}
          </Box>
        )}

        {/* Results count display */}
        {totalCount > 0 && (
          <Box textAlign="center" p={2}>
            <Typography variant="body2" color="text.secondary">
              {t('documents.resultsCount', {
                shown: sortedDocuments.length,
                total: totalCount,
              })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 共有モーダル */}
      <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)}>
        <DialogTitle>{t('documents.shareDocument')}</DialogTitle>
        <DialogContent>
          <Typography>{t('documents.shareComingSoon')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareModalOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* タグ編集ダイアログ */}
      <Dialog open={tagEditOpen} onClose={() => setTagEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('chat.editTags')}</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={tagEditValue}
            onChange={(_, newValue) => setTagEditValue(newValue as string[])}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <MuiTextField {...params} variant="outlined" placeholder={t('chat.tagInputPlaceholder')} size="small" sx={{ mt: 1 }} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagEditOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (tagEditDocId && onUpdateTags) {
                onUpdateTags(tagEditDocId, tagEditValue);
              }
              setTagEditOpen(false);
              setTagEditDocId(null);
            }}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('chat.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (deleteTargetId && onDelete) {
                onDelete(deleteTargetId);
              }
              setDeleteDialogOpen(false);
              setDeleteTargetId(null);
            }}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentListView;
